import { createSlice } from "@reduxjs/toolkit";
import {
  fetchUsers,
  createUser,
  deleteUser,
  updateUser,
  fetchCarePlanMdtUsers,
} from "../actions/userActions";

const initialMdtState = {
  assigned_users: [],
  unassigned_users: [],
  assigned_pagination: {},
  unassigned_pagination: {},
  assignedPage: 1,
  assignedRowsPerPage: 12,
  unassignedPage: 1,
  unassignedRowsPerPage: 12,
  searchQuery: { name: "", email: "" },
};

const initialState = {
  users: {},
  userTotalRows: 0,
  userCurrentPage: 1,
  userRowsPerPage: 12,
  userSearchQuery: { name: "", email: "" },
  userLoading: false,
  userError: null,
  mdtUsersData: initialMdtState,
  mdtUsersLoading: false,
  mdtUsersError: null,
};

const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    setCurrentPage(state, action) {
      state.userCurrentPage = action.payload;
    },
    setRowsPerPage(state, action) {
      state.userRowsPerPage = action.payload;
      state.userCurrentPage = 1; // reset page if user changes rowsPerPage
    },
    setFilters(state, action) {
      state.userSearchQuery = action.payload;
      state.userCurrentPage = 1;
      state.users = {}; // Clear cache to force API call on filter change
    },
    invalidatePagesAfter: (state, action) => {
      const { fromPage } = action.payload;
      Object.keys(state.users)
        .map(Number)
        .forEach((pageNum) => {
          if (pageNum > fromPage) {
            delete state.users[pageNum];
          }
        });
    },
    setAssignedPage: (s, a) => {
      s.mdtUsersData.assignedPage = a.payload;
    },
    setAssignedRowsPerPage: (s, a) => {
      s.mdtUsersData.assignedRowsPerPage = a.payload;
      s.mdtUsersData.assignedPage = 1;
    },
    setUnassignedPage: (s, a) => {
      s.mdtUsersData.unassignedPage = a.payload;
    },
    setUnassignedRowsPerPage: (s, a) => {
      s.mdtUsersData.unassignedRowsPerPage = a.payload;
      s.mdtUsersData.unassignedPage = 1;
    },
    setMdtFilters: (s, a) => {
      s.mdtUsersData.searchQuery = a.payload;
      s.mdtUsersData.assignedPage = 1;
      s.mdtUsersData.unassignedPage = 1;
    },
  },
  extraReducers: (builder) => {
    // fetchUserList
    builder.addCase(fetchUsers.fulfilled, (state, action) => {
      const { data, totalRows, page, rowsPerPage, searchQuery } =
        action.payload;

      // If rowsPerPage or searchQuery changed, reset and then set:
      const rowsOrSearchChanged =
        state.userRowsPerPage !== rowsPerPage ||
        state.userSearchQuery.name !== (searchQuery?.name || "") ||
        state.userSearchQuery.email !== (searchQuery?.email || "");

      if (rowsOrSearchChanged) {
        state.users = {};
        state.userTotalRows = 0;
        state.userCurrentPage = 1;
      }

      state.userRowsPerPage = rowsPerPage;
      state.userSearchQuery = searchQuery || {
        name: "",
        email: "",
      };
      state.userCurrentPage = page;
      state.users[page] = data;
      state.userTotalRows = totalRows;
      state.userLoading = false;
    });

    // Create User
    builder.addCase(createUser.fulfilled, (state, action) => {
      // Reset state to allow correct pagination on first page refresh
      state.userLoading = false;
    });

    // removeUser
    builder.addCase(deleteUser.fulfilled, (state, action) => {
      state.userLoading = false;
      // Or forcibly refetch if you want correct pagination from server
    });

    // update user
    builder.addCase(updateUser.fulfilled, (state, action) => {
      const updated = action.payload; // the updated user
      // We can find the user in state.users if we do a re-fetch
      // or we can manually replace the old user in the current page

      // Example: manual approach
      const currentPage = state.userCurrentPage;
      const currentList = state.users[currentPage] || [];
      const idx = currentList.findIndex((u) => u.id === updated.id);
      if (idx !== -1) {
        currentList[idx] = updated;
      }
      state.users[currentPage] = [...currentList];
    });

    builder.addCase(fetchCarePlanMdtUsers.fulfilled, (s, { payload }) => {
      Object.assign(s.mdtUsersData, {
        assigned_users: payload.assigned_users,
        unassigned_users: payload.unassigned_users,
        assigned_pagination: payload.assigned_pagination,
        unassigned_pagination: payload.unassigned_pagination,
        // pages & sizes already in payload
      });
      s.mdtUsersLoading = false;
    });
  },
});

export const {
  setCurrentPage,
  setRowsPerPage,
  setFilters,
  invalidatePagesAfter,
  setMdtFilters,
  setAssignedPage,
  setAssignedRowsPerPage,
  setUnassignedPage,
  setUnassignedRowsPerPage,
  // setAssignedSearch,
  // setUnassignedSearch,
} = userSlice.actions;
export default userSlice.reducer;
